import * as d3 from 'd3-shape';
import { arcAt, connectPointsWithRadius, rotateBy } from './index';

/**
 * @typedef {{x:number,y:number}} POINT
 * @param {POINT} pt
 * @param {number} r
 * @returns {POINT}
 */
function multiplyPoint(pt, r) {
    return {
        x: pt.x * r,
        y: pt.y * r
    };
}

/**
 * 
 * @param {POINT} p1 
 * @param {POINT} p2 
 * @returns {POINT}
 */
function addPoints(p1, p2) {
    return {
        x: p1.x + p2.x,
        y: p1.y + p2.y
    };
}

/**
 * 
 * @param {POINT} pt 
 */
function pointDist(pt) {
    return Math.sqrt(Math.pow(pt.x, 2) + Math.pow(pt.y, 2));
}

/**
 * do something
 * @param {POINT} p1 
 * @param {POINT} p2 
 */
function drawSide(p1, p2, concave) {
    const concaveCount = concave ? -1 : 1;
    const ret = [];
    const unit = {
        x: p2.x - p1.x,
        y: p2.y - p1.y
    };

    const dist = pointDist(unit);
    const normUnit = multiplyPoint(unit, 1 / dist);
    // const center = multiplyPoint(addPoints(p1, p2), .5);
    const firstRadiusRatio = .5 / Math.sin(Math.PI / 8);
    const firstRadius = dist * firstRadiusRatio;
    const firstCenter = rotateBy(addPoints(p1, multiplyPoint(unit, firstRadiusRatio)), p1, concaveCount * 67.5);
    const firstStop = rotateBy(p1, firstCenter, concaveCount * 15);

    ret.push(`A${firstRadius} ${firstRadius} 0 0 ${concave ? 0 : 1} ${firstStop.x} ${firstStop.y}`);

    const thirdCenter = addPoints(rotateBy(multiplyPoint(unit, .66), multiplyPoint(unit, .5), concaveCount * 90), p1);
    const thirdRadius = .16 * dist;

    // #region calculate a point
    const firstNorm = rotateBy(normUnit, { x: 0, y: 0 }, concaveCount * 82.5);
    // thirdCenter - firstStop
    const vec = addPoints(thirdCenter, multiplyPoint(firstStop, -1));
    const projLen = vec.x * firstNorm.x + vec.y * firstNorm.y;
    const vecLen = pointDist(vec);
    const ht = vecLen * Math.sin(Math.acos(projLen / vecLen));

    console.log([projLen, vecLen, ht]);

    const secondRadius = Math.abs((Math.pow(thirdRadius, 2) - Math.pow(ht, 2) - Math.pow(projLen, 2)) / 2 / (projLen + thirdRadius));
    const alpha = Math.asin(ht / (secondRadius + thirdRadius));
    const secondCenter = addPoints(multiplyPoint(firstNorm, secondRadius), firstStop);
    const secondStop = rotateBy(firstStop, secondCenter, concaveCount * (180 - alpha * 180 / Math.PI));
    ret.push(`A${secondRadius} ${secondRadius} 0 0 ${concave ? 0 : 1} ${secondStop.x} ${secondStop.y}`);

    // #endregion

    const secondToCenter = addPoints(thirdCenter, multiplyPoint(secondStop, -1));
    const secondStopProj = normUnit.x * secondToCenter.x + normUnit.y * secondToCenter.y;
    const thirdStop = addPoints(secondStop, multiplyPoint(normUnit, secondStopProj * 2));

    ret.push(`A${thirdRadius} ${thirdRadius} 0 1 ${concave ? 1 : 0} ${thirdStop.x} ${thirdStop.y}`);
    const firstToCenter = addPoints(thirdCenter, multiplyPoint(firstStop, -1));
    const firstStopProj = normUnit.x * firstToCenter.x + normUnit.y * firstToCenter.y;
    const fourthStop = addPoints(firstStop, multiplyPoint(normUnit, firstStopProj * 2));

    ret.push(`A${secondRadius} ${secondRadius} 0 0 ${concave ? 0 : 1} ${fourthStop.x} ${fourthStop.y}`);

    ret.push(`A${firstRadius} ${firstRadius} 0 0 ${concave ? 0 : 1} ${p2.x} ${p2.y}`);

    return ret.join('');
}

/**
 * 
 * @param {POINT[]} points 
 * @param {number} radius 
 * @param {number} aspectRatio 
 * @returns 
 */
function connectContainerPointsWithRadius(points, radius, aspectRatio) {
    return points.map((point, i) => {
        const prevPoint = points[(i - 1 + points.length) % points.length];
        const nextPoint = points[(i + 1) % points.length];
        const { intersection: [first, second], counterclockwise } = arcAt(point, prevPoint, nextPoint, radius);
        return `${i === 0 ? 'M' : 'L'}${first.x / aspectRatio} ${first.y}A${radius / aspectRatio} ${radius} 0 0 ${counterclockwise ? 0 : 1} ${second.x / aspectRatio} ${second.y}`;
    }).join(' ') + 'z';
}

export default {
    arc(params) {
        const strokeWidth = (params.strokeWidth || 0) * (params.strokeNumeric ? 1 : .01);
        const radius = params.strokeNumeric ? params.width * params.zoom / 2 : 1;
        return [{
            tag: 'path',
            attrs: {
                d: d3.arc().cornerRadius(params.cornerRadius * radius)({
                    innerRadius: params.innerRadius * radius + strokeWidth / 2,
                    outerRadius: radius - strokeWidth / 2,
                    startAngle: params.startAngle * Math.PI / 180,
                    endAngle: params.endAngle * Math.PI / 180
                }),
                fill: params.color,
                stroke: params.stroke,
                'stroke-width': strokeWidth
            }
        }];
    },
    rect(params) {
        const aspectRatio = params.width / params.height;
        const strokeWidth = (params.strokeWidth || 0) * (params.strokeNumeric ? 1 : .01);
        const totalWidth = params.strokeNumeric ? params.width * params.zoom : 2 * aspectRatio;
        const totalHeight = params.strokeNumeric ? params.height * params.zoom : 2;
        const radius = (params.radius || 0) / 100;
        return [{
            tag: 'rect',
            attrs: {
                width: totalWidth - strokeWidth,
                height: totalHeight - strokeWidth,
                x: strokeWidth / 2 - totalWidth / 2,
                y: strokeWidth / 2 - totalHeight / 2,
                fill: params.color,
                stroke: params.stroke,
                'stroke-width': strokeWidth,
                rx: radius * totalHeight / 2
            }
        }];
    },
    arrow(params) {
        const aspectRatio = params.width / params.height;
        const strokeWidth = (params.strokeWidth || 0) * (params.strokeNumeric ? 1 : .01);
        const scale = params.strokeNumeric ? params.height * params.zoom / 2 : 1;
        const radius = (params.radius || 0) * scale / 100;
        const w = (params.arrowWidth || .3) * scale;
        const x = aspectRatio * scale - strokeWidth / Math.SQRT2;
        const y = scale - strokeWidth / Math.SQRT2;
        const usedWidth = Math.max(0, w - strokeWidth);
        return [{
            tag: 'path',
            attrs: {
                d: connectPointsWithRadius([
                    { x, y: 0 }, { x: x - y, y: -y }, { x: x - y - usedWidth / Math.SQRT2, y: -y + usedWidth / Math.SQRT2 },
                    { x: x - usedWidth * (.5 + Math.SQRT2), y: -usedWidth / 2 },
                    { x: strokeWidth / 2 - aspectRatio * scale, y: -usedWidth / 2 }, { x: strokeWidth / 2 - aspectRatio * scale, y: usedWidth / 2 },
                    { x: x - usedWidth * (.5 + Math.SQRT2), y: usedWidth / 2 },
                    { x: x - y - usedWidth / Math.SQRT2, y: y - usedWidth / Math.SQRT2 }, { x: x - y, y }
                ], radius),
                fill: params.color,
                stroke: params.stroke,
                'stroke-width': strokeWidth
            }
        }];
    },
    ellipse(params) {
        const w = params.strokeNumeric ? params.width * params.zoom / 2 : params.width / params.height;
        const h = params.strokeNumeric ? params.height * params.zoom / 2 : 1;
        const strokeWidth = (params.strokeWidth || 0) * (params.strokeNumeric ? 1 : .01);
        return [{
            tag: 'ellipse',
            attrs: {
                rx: w - strokeWidth / 2,
                ry: h - strokeWidth / 2,
                cx: 0,
                cy: 0,
                fill: params.color,
                stroke: params.stroke,
                'stroke-width': strokeWidth
            }
        }];
    },
    star(params) {
        const strokeWidth = (params.strokeWidth || 0) * (params.strokeNumeric ? 1 : .01);
        const points = params && params.points || 5;
        const scale = params.strokeNumeric ? params.width * params.zoom / 2 : 1;
        const radius = (params.radius || 0) / 100 * scale;

        const pointAngle = Math.atan(
            (params.innerRadius * Math.sin(Math.PI / points))
            / (1 - params.innerRadius * Math.cos(Math.PI / points))
        );
        // consider stroke-meterlimit
        console.log(pointAngle * 180 / Math.PI);
        let usedStrokeWidth = Math.abs(strokeWidth / 2 / Math.sin(pointAngle));
        if (params.innerRadius === 0 || usedStrokeWidth / strokeWidth > 2) {
            usedStrokeWidth = 0;
        }
        const pointCoords = [].concat.apply([], Array.apply(null, { length: points }).map((_, i) => {
            const deg = i * 360 / points;
            const point = rotateBy({ x: 0, y: usedStrokeWidth - scale }, { x: 0, y: 0 }, deg);
            const nextPoint = rotateBy(
                { x: 0, y: (usedStrokeWidth - scale) * params.innerRadius },
                { x: 0, y: 0 },
                deg + 180 / points
            );
            return [point, nextPoint];
        }));
        return [{
            tag: 'path',
            attrs: {
                d: connectPointsWithRadius(pointCoords, radius),
                fill: params.color,
                stroke: params.stroke,
                'stroke-width': strokeWidth
            }
        }];
    },
    polygon(params) {
        const strokeWidth = (params.strokeWidth || 0) * (params.strokeNumeric ? 1 : .01);
        const points = params && params.points || 6;
        const scale = params.strokeNumeric ? params.width * params.zoom / 2 : 1;
        const radius = (params.radius || 0) / 100 * scale;

        const pointAngle = (points - 2) / points / 2 * Math.PI;
        // consider stroke-meterlimit
        let usedStrokeWidth = Math.abs(strokeWidth / 2 / Math.sin(pointAngle));
        const pointCoords = Array.apply(null, { length: points }).map((_, i) => {
            const deg = i * 360 / points;
            return rotateBy({ x: 0, y: usedStrokeWidth - scale }, { x: 0, y: 0 }, deg);
        });
        return [{
            tag: 'path',
            attrs: {
                d: connectPointsWithRadius(pointCoords, radius),
                fill: params.color,
                stroke: params.stroke,
                'stroke-width': strokeWidth
            }
        }];
    },
    heart(params) {
        const strokeWidth = (params.strokeWidth || 0) * (params.strokeNumeric ? 1 : .01);
        const scale = (params.strokeNumeric ? params.width * params.zoom / 2 : 1) - strokeWidth / 2;
        const coords = [0, -.5, -.4, -1.6, -2, -.5, 0, .9, 2, -.5, .4, -1.6, 0, -.5].map(t => t * scale);

        return [{
            tag: 'path',
            attrs: {
                d: `M${coords.slice(0, 2).join(' ')}C${coords.slice(2, 8).join(' ')}C${coords.slice(8).join(' ')}`,
                fill: params.color,
                stroke: params.stroke,
                'stroke-width': strokeWidth
            }
        }];
    },
    cat(params) {
        const strokeWidth = (params.strokeWidth || 0) * 10;
        return [
            // {
            //     tag: 'filter',
            //     attrs: {
            //         id: 'filter' + params.effectId
            //     },
            //     children: [
            //         {
            //             tag: 'feGaussianBlur',
            //             attrs: {
            //                 in: 'SourceAlpha',
            //                 stdDeviation: 60
            //             }
            //         },
            //         {
            //             tag: 'feOffset',
            //             attrs: {
            //                 result: 'offset',
            //                 dx: 0,
            //                 dy: 18
            //             }
            //         },
            //         {
            //             tag: 'feFlood',
            //             attrs: {
            //                 'flood-color': 'rgba(0,0,0,.1)'
            //             }
            //         },
            //         {
            //             tag: 'feComposite',
            //             attrs: {
            //                 operator: 'in',
            //                 in2: 'offset',
            //                 result: 'blur'
            //             }
            //         },
            //         {
            //             tag: 'feGaussianBlur',
            //             attrs: {
            //                 in: 'SourceAlpha',
            //                 stdDeviation: 22,
            //                 result: 'blur1'
            //             }
            //         },
            //         {
            //             tag: 'feFlood',
            //             attrs: {
            //                 'flood-color': 'rgba(0,0,0,.06)'
            //             }
            //         },
            //         {
            //             tag: 'feComposite',
            //             attrs: {
            //                 operator: 'in',
            //                 in2: 'blur1',
            //                 result: 'blur2'
            //             }
            //         },
            //         {
            //             tag: 'feMerge',
            //             attrs: {},
            //             children: [
            //                 {
            //                     tag: 'feMergeNode',
            //                     attrs: {
            //                         in: 'blur'
            //                     }
            //                 },
            //                 {
            //                     tag: 'feMergeNode',
            //                     attrs: {
            //                         in: 'blur2'
            //                     }
            //                 },
            //                 {
            //                     tag: 'feMergeNode',
            //                     attrs: {
            //                         in: 'SourceGraphic'
            //                     }
            //                 }
            //             ]
            //         }
            //     ]
            // },
            {
                tag: 'path',
                attrs: {
                    d: 'M0 972C1 773 36 682 92 599C58 453 43 198 47 0C220 57 495 211 608 346C706 335 852 335 953 342C1065 204 1325 64 1516 16C1516 211 1483 457 1461 577C1524 648 1572 766 1572 961C1572 1228 1373 1401 1092 1442C926 1466 671 1464 465 1443C179 1413 -1 1246 0 972z',
                    fill: params.color,
                    transform: 'translate(-.8 -.7) scale(.001)',
                    stroke: params.stroke,
                    'stroke-width': strokeWidth
                }
            },
            {
                tag: 'circle',
                attrs: {
                    fill: params.eyeColor,
                    cx: -.35,
                    cy: .26,
                    r: .08
                }
            },
            {
                tag: 'circle',
                attrs: {
                    fill: params.eyeColor,
                    cx: .32,
                    cy: .26,
                    r: .08
                }
            },
            {
                tag: 'path',
                attrs: {
                    d: d3.arc().cornerRadius(1)({
                        innerRadius: .93,
                        outerRadius: 1,
                        startAngle: -.4,
                        endAngle: .22
                    }),
                    fill: params.stroke,
                    transform: 'translate(-.79 .75) scale(.41)'
                }
            },
            {
                tag: 'path',
                attrs: {
                    d: d3.arc().cornerRadius(1)({
                        innerRadius: .93,
                        outerRadius: 1,
                        startAngle: -.65,
                        endAngle: -.05
                    }),
                    fill: params.stroke,
                    transform: 'translate(-.66 .85) scale(.41)'
                }
            },
            {
                tag: 'path',
                attrs: {
                    d: d3.arc().cornerRadius(1)({
                        innerRadius: .93,
                        outerRadius: 1,
                        startAngle: -.22,
                        endAngle: .4
                    }),
                    fill: params.stroke,
                    transform: 'translate(.75 .75) scale(.41)'
                }
            },
            {
                tag: 'path',
                attrs: {
                    d: d3.arc().cornerRadius(1)({
                        innerRadius: .93,
                        outerRadius: 1,
                        startAngle: .05,
                        endAngle: .65
                    }),
                    fill: params.stroke,
                    transform: 'translate(.62 .85) scale(.41)'
                }
            }
        ]
    },
    path(params) {
        const strokeWidth = (params.strokeWidth || 0) / 100 / params.shapeScale;
        return [{
            tag: 'path',
            attrs: {
                d: params.d,
                fill: params.color,
                stroke: params.stroke,
                'stroke-width': strokeWidth,
                transform: `translate(${params.shapeX} ${params.shapeY}) scale(${params.shapeScale})`
            }
        }];
    },
    ring(params) {
        const strokeWidth = (params.strokeWidth || 0) * (params.strokeNumeric ? 1 : .01);
        const usedOuterRadius = (params.strokeNumeric ? params.width * params.zoom / 2 : 1) - strokeWidth / 2;
        const usedInnerRadius = params.innerRadius * usedOuterRadius;
        return [{
            tag: 'path',
            attrs: {
                d: `M0 ${-usedOuterRadius}A${usedOuterRadius} ${usedOuterRadius} 0 0 0 0 ${usedOuterRadius}A${usedOuterRadius} ${usedOuterRadius} 0 0 0 0 ${-usedOuterRadius}zM0 ${-usedInnerRadius}A${usedInnerRadius} ${usedInnerRadius} 0 0 1 0 ${usedInnerRadius}A${usedInnerRadius} ${usedInnerRadius} 0 0 1 0 ${-usedInnerRadius}z`,
                fill: params.color,
                stroke: params.stroke,
                'stroke-width': strokeWidth
            }
        }];
    },
    triangle(params) {
        const outerX = params.strokeNumeric ? params.width / 2 * params.zoom : params.width / params.height;
        const outerY = params.strokeNumeric ? params.height / 2 * params.zoom : 1;
        const strokeWidth = (params.strokeWidth || 0) * (params.strokeNumeric ? 1 : .01);
        const radius = (params.radius || 0) / 100 * outerY;
        const x = outerX - strokeWidth;
        const usedPointPos = params.pointPos * (outerX - strokeWidth);
        return [{
            tag: 'path',
            attrs: {
                d: connectPointsWithRadius([
                    { x: usedPointPos, y: strokeWidth - outerY },
                    { x: -x, y: outerY - strokeWidth / 2 },
                    { x: x, y: outerY - strokeWidth / 2 }
                ], radius),
                fill: params.color,
                stroke: params.stroke,
                'stroke-width': strokeWidth
            }
        }];
    },
    diamond(params) {
        const outerX = params.strokeNumeric ? params.width / 2 * params.zoom : params.width / params.height;
        const outerY = params.strokeNumeric ? params.height / 2 * params.zoom : 1;
        const strokeWidth = (params.strokeWidth || 0) * (params.strokeNumeric ? 1 : .01);
        const radius = (params.radius || 0) / 100 * outerY;
        const a = Math.atan(params.width / params.height);
        const x = outerX - strokeWidth / Math.cos(a) / 2;
        const y = outerY - strokeWidth / Math.sin(a) / 2;
        return [{
            tag: 'path',
            attrs: {
                d: connectPointsWithRadius([{ x: 0, y: -y }, { x: -x, y: 0 }, { x: 0, y }, { x, y: 0 }], radius),
                fill: params.color,
                stroke: params.stroke,
                'stroke-width': strokeWidth
            }
        }];
    },
    rabbit(params) {
        const strokeWidth = (params.strokeWidth || 0) * 10;
        return [
            {
                tag: 'path',
                attrs: {
                    d: 'M14 1586C14 1312 356 1033 535 1020C367 931 -97 577 18 191C92 -57 494 -68 644 181C757 369 779 696 788 1014C811 1015 833 1009 848 1013C869 730 935 336 1042 172C1204 -76 1578 -36 1665 178C1823 568 1236 966 1083 1025C1263 1030 1586 1294 1586 1575C1586 1842 1387 2015 1106 2056C940 2080 685 2078 479 2057C193 2027 14 1860 14 1586z',
                    fill: params.color,
                    transform: 'translate(-.75 -.95) scale(.0009)',
                    stroke: params.stroke,
                    'stroke-width': strokeWidth
                }
            },
            {
                tag: 'circle',
                attrs: {
                    fill: params.eyeColor,
                    cx: -.32,
                    cy: .53,
                    r: .08
                }
            },
            {
                tag: 'circle',
                attrs: {
                    fill: params.eyeColor,
                    cx: .27,
                    cy: .53,
                    r: .08
                }
            }
        ]
    },
    trapezoid(params) {
        const aspectRatio = params.width / params.height;
        const outerX = params.strokeNumeric ? params.width / 2 * params.zoom : aspectRatio;
        const outerY = params.strokeNumeric ? params.height / 2 * params.zoom : 1;
        const strokeWidth = (params.strokeWidth || 0) * (params.strokeNumeric ? 1 : .01);
        const radius = (params.radius || 0) / 100 * outerY;

        let { skewLeft, skewRight } = params;
        skewLeft *= aspectRatio;
        skewRight *= aspectRatio;

        const usedY = outerY - strokeWidth / 2;
        const leftAngle = Math.atan(2 / Math.abs(skewLeft));
        const leftOffset = strokeWidth / Math.tan(leftAngle / 2) / 2;

        const rightAngle = Math.atan(2 / Math.abs(skewRight));
        const rightOffset = strokeWidth / Math.tan(rightAngle / 2) / 2;

        const topLeft = {
            y: -usedY,
            x: (skewLeft > 0 ? -outerX + skewLeft * outerY : -outerX) + leftOffset
        };

        const bottomLeft = {
            y: usedY,
            x: (skewLeft > 0 ? -outerX : -outerX - skewLeft * outerY) + leftOffset
        };

        const bottomRight = {
            y: usedY,
            x: (skewRight > 0 ? outerX - skewRight * outerY : outerX) - rightOffset
        };

        const topRight = {
            y: -usedY,
            x: (skewRight > 0 ? outerX : outerX + skewRight * outerY) - rightOffset
        };

        return [{
            tag: 'path',
            attrs: {
                stroke: params.stroke,
                'stroke-width': strokeWidth,
                fill: params.color,
                d: connectPointsWithRadius([topLeft, bottomLeft, bottomRight, topRight], radius)
            }
        }];
    },
    arcArrow(params) {
        const { outerRadius: origOutRadius, arrowWidth: origArrowWidth, startAngle, endAngle, counterclockwise } = params;
        const strokeWidth = (params.strokeWidth || 0) * (params.strokeNumeric ? 1 : .01);
        const scale = params.strokeNumeric ? params.zoom * params.width / 2 : 1;
        const outerRadius = origOutRadius * scale, arrowWidth = origArrowWidth * scale;

        const usedRadius = outerRadius - arrowWidth / 2;
        const innerRadius = outerRadius - arrowWidth;
        // const end = { x: 0, y: -usedRadius };

        const angle = Math.atan(arrowWidth * (.5 + Math.SQRT2) / usedRadius);
        const angleDegrees = angle * 180 / Math.PI;
        const origin = { x: 0, y: 0 };

        console.log(angleDegrees);
        const arcTopIn = rotateBy({ x: 0, y: -outerRadius }, origin, -angleDegrees);
        const arcBottomIn = rotateBy({ x: 0, y: -innerRadius }, origin, -angleDegrees);

        const endY = usedRadius / Math.cos(angle);
        const usedEnd = { x: 0, y: -endY };
        const rotateDegrees = (Math.acos(endY / scale * Math.sin(Math.PI / 4 + angle)) || 0) * 180 / Math.PI - 45 + angleDegrees;
        const arrowTop = rotateBy({ x: 0, y: -scale }, origin, -rotateDegrees);
        const arrowTopIn = rotateBy({ x: 0, y: arrowWidth }, origin, 45 - angleDegrees);
        arrowTopIn.x += arrowTop.x;
        arrowTopIn.y += arrowTop.y;

        const arrowBottom = rotateBy(arrowTop, usedEnd, -90);
        const arrowBottomIn = rotateBy({ x: 0, y: arrowWidth }, origin, 135 - angleDegrees);
        arrowBottomIn.x += arrowBottom.x;
        arrowBottomIn.y += arrowBottom.y;

        let d;

        if (counterclockwise) {
            [arrowTop, arrowTopIn, arcTopIn, arcBottomIn, arrowBottomIn, arrowBottom].forEach(pt => {
                pt.x = -pt.x; // flip horizontally
            });
            [usedEnd, arrowTop, arrowTopIn, arcTopIn, arcBottomIn, arrowBottomIn, arrowBottom].forEach(pt => {
                Object.assign(pt, rotateBy(pt, origin, startAngle));
            });
            const startOut = rotateBy({ x: 0, y: -outerRadius }, origin, endAngle);
            const startIn = rotateBy({ x: 0, y: -innerRadius }, origin, endAngle);

            const arcEndAngle = startAngle + angleDegrees;
            const drawLargeArc = (endAngle - arcEndAngle + 360) % 360 > 180;
            // TODO: scale the shape

            d = `M${usedEnd.x} ${usedEnd.y}L${arrowBottom.x} ${arrowBottom.y}L${arrowBottomIn.x} ${arrowBottomIn.y}L${arcBottomIn.x} ${arcBottomIn.y}A${innerRadius} ${innerRadius} 0 ${drawLargeArc ? 1 : 0} 1 ${startIn.x} ${startIn.y}L${startOut.x} ${startOut.y}A${outerRadius} ${outerRadius} 0 ${drawLargeArc ? 1 : 0} 0 ${arcTopIn.x} ${arcTopIn.y}L${arrowTopIn.x} ${arrowTopIn.y}L${arrowTop.x} ${arrowTop.y}z`;
        } else {
            [usedEnd, arrowTop, arrowTopIn, arcTopIn, arcBottomIn, arrowBottomIn, arrowBottom].forEach(pt => {
                Object.assign(pt, rotateBy(pt, origin, endAngle));
            });
            const startOut = rotateBy({ x: 0, y: -outerRadius }, origin, startAngle);
            const startIn = rotateBy({ x: 0, y: -innerRadius }, origin, startAngle);
            const arcEndAngle = endAngle - angleDegrees;
            const drawLargeArc = (arcEndAngle - startAngle + 360) % 360 > 180;

            // TODO: draw the two intersection points between the two arcs and the two inner arrow lines

            d = `M${usedEnd.x} ${usedEnd.y}L${arrowTop.x} ${arrowTop.y}L${arrowTopIn.x} ${arrowTopIn.y}L${arcTopIn.x} ${arcTopIn.y}A${outerRadius} ${outerRadius} 0 ${drawLargeArc ? 1 : 0} 0 ${startOut.x} ${startOut.y}L${startIn.x} ${startIn.y}A${innerRadius} ${innerRadius} 0 ${drawLargeArc ? 1 : 0} 1 ${arcBottomIn.x} ${arcBottomIn.y}L${arrowBottomIn.x} ${arrowBottomIn.y}L${arrowBottom.x} ${arrowBottom.y}z`;
        }

        return [{
            tag: 'path',
            attrs: {
                stroke: params.stroke,
                'stroke-width': strokeWidth,
                fill: params.color,
                d
            }
        }];
    },
    teardrop(params) {
        const strokeWidth = (params.strokeWidth || 0) * (params.strokeNumeric ? 1 : .01);
        const scale = params.strokeNumeric ? params.zoom * params.width / 2 : 1;
        const controlPosRatio = typeof params.controlPosition === 'undefined' ? 1 : params.controlPosition;
        const factor = controlPosRatio > 1
            ? 1 / (strokeWidth / scale * (Math.cos(Math.PI / 4 - Math.atan(.25)) * Math.sqrt(17) / 2 - .5) + (controlPosRatio + 1) / 2)
            : 1;
        const usedRadius = factor * scale - strokeWidth / 2;
        const controlPos = controlPosRatio * usedRadius;
        const offset = (factor - 1) * scale;
        return [{
            tag: 'path',
            attrs: {
                stroke: params.stroke,
                'stroke-width': strokeWidth,
                fill: params.color,
                d: `M${controlPos + offset} ${-controlPos - offset}Q${controlPos / 2 + offset} ${-usedRadius - offset} ${offset} ${-usedRadius - offset}A${usedRadius} ${usedRadius} 0 1 0 ${usedRadius + offset} ${-offset}Q${usedRadius + offset} ${-controlPos / 2 - offset} ${controlPos + offset} ${-controlPos - offset}z`
            }
        }];
    },
    chatBubble(params) {
        const strokeWidth = (params.strokeWidth || 0) * (params.strokeNumeric ? 1 : .01);
        const scale = params.strokeNumeric ? params.zoom * params.height / 2 : 1;

        const aspectRatio = params.width / params.height;
        const usedWidth = aspectRatio * scale;
        const tipSize = Math.min(Math.SQRT1_2, aspectRatio * Math.SQRT1_2 / (1 + Math.SQRT1_2 / 2));
        const usedTipSize = tipSize * scale;
        const radius = (params.radius || 0) / 100 * Math.min(1, aspectRatio - tipSize / 2) * scale;

        const points = [
            { x: -usedWidth + strokeWidth / 2 + usedTipSize, y: strokeWidth / 2 - scale },
            { x: -usedWidth + strokeWidth / 2 + usedTipSize, y: scale - strokeWidth / 2 },
            { x: usedWidth - strokeWidth / 2, y: scale - strokeWidth / 2 },
            { x: usedWidth - strokeWidth / 2, y: strokeWidth / 2 - scale },
        ];
        const pointPath = [].concat.apply([], points.map((pt, i) => {
            if (i === 0) {
                return [];
            }
            if (radius) {
                const {
                    intersection: [first, second],
                    counterclockwise
                } = arcAt(pt, points[i - 1], points[(i + 1) % 4], radius);
                return `L${first.x} ${first.y}A${radius} ${radius} 0 0 ${counterclockwise ? 0 : 1} ${second.x} ${second.y}`;
            }
            return `L${pt.x} ${pt.y}`;
        }));

        const maxRadius = (1 - tipSize * (1 + Math.SQRT1_2) + Math.cos(Math.asin(1 - Math.SQRT1_2)) * tipSize) * scale;
        let tipIntersection;
        if (radius < maxRadius || aspectRatio < 1 + Math.SQRT2 / 4) {
            tipIntersection = {
                x: -usedWidth + strokeWidth / 2 + usedTipSize,
                y: maxRadius - scale
            }
            const {
                intersection: [first, second],
                counterclockwise
            } = arcAt(points[0], points[3], tipIntersection, Math.min(radius, maxRadius), maxRadius);
            pointPath.push(`L${first.x} ${first.y}A${radius} ${radius} 0 0 ${counterclockwise ? 0 : 1} ${second.x} ${second.y}L${tipIntersection.x} ${tipIntersection.y}`);
        } else {
            const u = (tipSize * (1 + Math.SQRT1_2) - 1) * scale + radius;
            const a = Math.acos(Math.min(1, (Math.pow(tipSize, 2) + 2 * Math.pow(u, 2) - Math.pow(radius, 2)) / (2 * tipSize * u * Math.SQRT2)));
            console.log(a);

            tipIntersection = {
                x: -usedWidth + strokeWidth / 2 + usedTipSize * (Math.sin(Math.PI / 4 - a) + Math.SQRT1_2),
                y: usedTipSize * (Math.cos(Math.PI / 4 - a) - 1 - Math.SQRT1_2)
            };
            console.log(tipIntersection);
            const first = {
                x: points[0].x + radius,
                y: points[0].y
            };
            pointPath.push(`L${first.x} ${first.y}A${radius} ${radius} 0 0 0 ${tipIntersection.x} ${tipIntersection.y}`);
        }

        return [{
            tag: 'path',
            attrs: {
                stroke: params.stroke,
                fill: params.color,
                'stroke-width': strokeWidth,
                d: `M${-usedWidth + strokeWidth / 2} ${-usedTipSize}A${usedTipSize} ${usedTipSize} 0 0 0 ${-usedWidth + strokeWidth / 2 + usedTipSize} 0${pointPath.join('')}A${usedTipSize} ${usedTipSize} 0 0 1 ${-usedWidth + strokeWidth / 2} ${-usedTipSize}z`
            }
        }];
    },
    cross(params) {
        const strokeWidth = (params.strokeWidth || 0) * (params.strokeNumeric ? 1 : .01);
        const scale = params.strokeNumeric ? params.zoom * params.height / 2 : 1;
        const drawPos = scale - strokeWidth / 2;
        const controlPos = params.controlPosition * drawPos / 3;
        const radius = (params.radius || 0) / 100 * scale;
        const points = [
            { x: -controlPos, y: -drawPos },
            { x: -controlPos, y: -controlPos },
            { x: -drawPos, y: -controlPos },
            { x: -drawPos, y: controlPos },
            { x: -controlPos, y: controlPos },
            { x: -controlPos, y: drawPos },
            { x: controlPos, y: drawPos },
            { x: controlPos, y: controlPos },
            { x: drawPos, y: controlPos },
            { x: drawPos, y: -controlPos },
            { x: controlPos, y: -controlPos },
            { x: controlPos, y: -drawPos }
        ];
        return [{
            tag: 'path',
            attrs: {
                stroke: params.stroke,
                fill: params.color,
                'stroke-width': strokeWidth,
                d: connectPointsWithRadius(points, radius)
            }
        }];
    },
    jigsaw(params) {
        const strokeWidth = (params.strokeWidth || 0) * (params.strokeNumeric ? 1 : .01);
        const scale = params.strokeNumeric ? params.zoom * params.height / 2 : 1;
        const pointPos = .603 * (scale - strokeWidth / 2);

        const points = [
            { x: -pointPos, y: -pointPos },
            { x: -pointPos, y: pointPos },
            { x: pointPos, y: pointPos },
            { x: pointPos, y: -pointPos }
        ];

        const edgeNames = ['leftEdge', 'bottomEdge', 'rightEdge', 'topEdge'];

        const d = [`M${points[0].x} ${points[0].y}`]
            .concat(points.map((pt, i) => {
                const next = points[(i + 1) % points.length];
                if (params[edgeNames[i]]) {
                    return `L${next.x} ${next.y}`;
                }
                return drawSide(pt, next, !(+params.horizontal ^ (i % 2)));
            }), 'z')
            .join('');

        return [{
            tag: 'path',
            attrs: {
                stroke: params.stroke,
                fill: params.color,
                'stroke-width': strokeWidth,
                d
            }
        }];
    },
    cloud(params) {
        // TODO:
        const strokeWidth = (params.strokeWidth || 0) * (params.strokeNumeric ? 1 : .01);
        const scale = params.strokeNumeric ? params.zoom * params.height / 2 : 1;
        const scaleFactor = scale - strokeWidth / 2;
        return [{
            tag: 'path',
            attrs: {
                stroke: params.stroke,
                fill: params.color,
                'stroke-width': strokeWidth,
                d: `M${-.6 * scaleFactor} ${.6 * scaleFactor}L${.6 * scaleFactor} ${.6 * scaleFactor}A${.4 * scaleFactor} ${.4 * scaleFactor} 0 0 0 ${.6 * scaleFactor} ${-.2 * scaleFactor}A${.6 * scaleFactor} ${.6 * scaleFactor} 0 0 0 ${-.6 * scaleFactor} ${-.2 * scaleFactor}A${.4 * scaleFactor} ${.4 * scaleFactor} 0 0 0 ${-.6 * scaleFactor} ${.6 * scaleFactor}z`
            }
        }];
    },
    line(params) {
        const aspectRatio = params.width / params.height;
        return [{
            tag: 'path',
            attrs: {
                stroke: params.stroke,
                'stroke-width': 2,
                d: `M${-aspectRatio} 0L${aspectRatio} 0`
            }
        }];
    },
    verticalLine(params) {
        const aspectRatio = params.width / params.height;
        return [{
            tag: 'path',
            attrs: {
                stroke: params.stroke,
                'stroke-width': 2,
                d: `M${-aspectRatio} 0L${aspectRatio} 0`
            }
        }];
    },
    starContainer(params) {
        const points = params && params.points || 5;

        return [{
            tag: 'path',
            attrs: {
                d: Array.apply(null, { length: points }).map((_, i) => {
                    const deg = i * 360 / points;
                    const { x, y } = rotateBy({ x: 0, y: - 1 }, { x: 0, y: 0 }, deg);
                    const { x: nextX, y: nextY } = rotateBy(
                        { x: 0, y: -params.innerRadius },
                        { x: 0, y: 0 },
                        deg + 180 / points
                    );
                    return `${i === 0 ? 'M' : 'L'}${x} ${y}L${nextX} ${nextY}`;
                }).join(' ') + 'z'
            }
        }];
    },
    polygonContainer(params) {
        const points = params && params.points || 6;
        const radius = (params.radius || 0) / 100;

        const pointCoords = Array.apply(null, { length: points }).map((_, i) => {
            const deg = i * 360 / points;
            return rotateBy({ x: 0, y: - 1 }, { x: 0, y: 0 }, deg);
        });
        return [{
            tag: 'path',
            attrs: {
                d: connectPointsWithRadius(pointCoords, radius)
            }
        }];
    },
    ringContainer(params) {
        const innerRadius = params.innerRadius;
        return [{
            tag: 'path',
            attrs: {
                d: `M0 -1A1 1 0 0 0 0 1A1 1 0 0 0 0 -1zM0 ${-innerRadius}A${innerRadius} ${innerRadius} 0 0 1 0 ${innerRadius}A${innerRadius} ${innerRadius} 0 0 1 0 ${-innerRadius}z`
            }
        }];
    },
    arcContainer(params) {
        return [{
            tag: 'path',
            attrs: {
                d: d3.arc().cornerRadius(params.cornerRadius)({
                    innerRadius: params.innerRadius,
                    outerRadius: 1,
                    startAngle: params.startAngle * Math.PI / 180,
                    endAngle: params.endAngle * Math.PI / 180
                })
            }
        }];
    },
    rectContainer(params) {
        const radius = (params.radius || 0) / 100;
        const aspectRatio = params.width / params.height;

        const pointCoords = [{ x: -aspectRatio, y: -1 }, { x: aspectRatio, y: -1 }, { x: aspectRatio, y: 1 }, { x: -aspectRatio, y: 1 }];

        return [{
            tag: 'path',
            attrs: {
                d: radius
                    ? pointCoords.map((point, i) => {
                        const prevPoint = pointCoords[(i - 1 + pointCoords.length) % pointCoords.length];
                        const nextPoint = pointCoords[(i + 1) % pointCoords.length];
                        const { intersection: [first, second], counterclockwise } = arcAt(point, prevPoint, nextPoint, radius);
                        return `${i === 0 ? 'M' : 'L'}${first.x / aspectRatio} ${first.y}A${radius / aspectRatio} ${radius} 0 0 ${counterclockwise ? 0 : 1} ${second.x / aspectRatio} ${second.y}`;
                    }).join(' ') + 'z'
                    : 'M-1 -1L-1 1L1 1L1 -1z'
            }
        }];
    },
    ellipseContainer() {
        return [{
            tag: 'path',
            attrs: {
                d: `M0 -1A1 1 0 0 0 0 1A1 1 0 0 0 0 -1z`
            }
        }];
    },
    triangleContainer(params) {
        return [{
            tag: 'path',
            attrs: {
                d: `M${params.pointPos} -1L-1 1L1 1z`
            }
        }];
    },
    heartContainer() {
        return [{
            tag: 'path',
            attrs: {
                d: 'M0 -.5C-.4 -1.6 -2 -.5 0 .9C2 -.5 .4 -1.6 0 -.5z'
            }
        }];
    },
    diamondContainer() {
        return [{
            tag: 'path',
            attrs: {
                d: `M0 -1L-1 0L0 1L1 0z`
            }
        }];
    },
    trapezoidContainer(params) {
        const radius = (params.radius || 0) / 100;
        const aspectRatio = params.width / params.height;

        let { skewLeft, skewRight } = params;
        skewLeft *= aspectRatio;
        skewRight *= aspectRatio;

        const topLeft = {
            y: -1,
            x: skewLeft > 0 ? -aspectRatio + skewLeft : -aspectRatio
        };

        const bottomLeft = {
            y: 1,
            x: skewLeft > 0 ? -aspectRatio : -aspectRatio - skewLeft
        };

        const bottomRight = {
            y: 1,
            x: skewRight > 0 ? aspectRatio - skewRight : aspectRatio
        };

        const topRight = {
            y: -1,
            x: skewRight > 0 ? aspectRatio : aspectRatio + skewRight
        }

        const pointCoords = [
            topLeft,
            topRight,
            bottomRight,
            bottomLeft
        ];

        return [{
            tag: 'path',
            attrs: {
                d: pointCoords.map((point, i) => {
                    if (!radius) {
                        return `${i === 0 ? 'M' : 'L'}${point.x / aspectRatio} ${point.y}`;
                    }
                    const prevPoint = pointCoords[(i - 1 + pointCoords.length) % pointCoords.length];
                    const nextPoint = pointCoords[(i + 1) % pointCoords.length];
                    const { intersection: [first, second], counterclockwise } = arcAt(point, prevPoint, nextPoint, radius);
                    return `${i === 0 ? 'M' : 'L'}${first.x / aspectRatio} ${first.y}A${radius / aspectRatio} ${radius} 0 0 ${counterclockwise ? 0 : 1} ${second.x / aspectRatio} ${second.y}`;
                }).join(' ') + 'z'
            }
        }];
    },
    rectBorderContainer(params) {
        const aspectRatio = params.width / params.height;
        const radius = Math.min((params.radius || 0) / 100, aspectRatio);
        const borderWidth = params.borderWidth / 100 * Math.min(1, aspectRatio);
        const innerRadius = Math.max(0, radius - borderWidth);

        const pointCoords = [{ x: -aspectRatio, y: -1 }, { x: aspectRatio, y: -1 }, { x: aspectRatio, y: 1 }, { x: -aspectRatio, y: 1 }];
        const innerPointCoords = [
            { x: borderWidth - aspectRatio, y: borderWidth - 1 },
            { x: aspectRatio - borderWidth, y: borderWidth - 1 },
            { x: aspectRatio - borderWidth, y: 1 - borderWidth },
            { x: borderWidth - aspectRatio, y: 1 - borderWidth }
        ];
        innerPointCoords.reverse();

        return [{
            tag: 'path',
            attrs: {
                d: [
                    connectContainerPointsWithRadius(pointCoords, radius, aspectRatio),
                    connectContainerPointsWithRadius(innerPointCoords, innerRadius, aspectRatio)
                ].join('')
            }
        }];
    },
    crossContainer(params) {
        const controlPos = params.controlPosition / 3;
        const radius = (params.radius || 0) / 100;
        const points = [
            { x: -controlPos, y: -1 },
            { x: -controlPos, y: -controlPos },
            { x: -1, y: -controlPos },
            { x: -1, y: controlPos },
            { x: -controlPos, y: controlPos },
            { x: -controlPos, y: 1 },
            { x: controlPos, y: 1 },
            { x: controlPos, y: controlPos },
            { x: 1, y: controlPos },
            { x: 1, y: -controlPos },
            { x: controlPos, y: -controlPos },
            { x: controlPos, y: -1 }
        ];
        return [{
            tag: 'path',
            attrs: {
                d: connectPointsWithRadius(points, radius)
            }
        }];
    },
    jigsawContainer(params) {
        const points = [
            { x: -.603, y: -.603 },
            { x: -.603, y: .603 },
            { x: .603, y: .603 },
            { x: .603, y: -.603 }
        ];

        const edgeNames = ['leftEdge', 'bottomEdge', 'rightEdge', 'topEdge'];

        const d = [`M${points[0].x} ${points[0].y}`]
            .concat(points.map((pt, i) => {
                const next = points[(i + 1) % points.length];
                if (params[edgeNames[i]]) {
                    return `L${next.x} ${next.y}`;
                }
                return drawSide(pt, next, !(+params.horizontal ^ (i % 2)));
            }), 'z')
            .join('');

        return [{
            tag: 'path',
            attrs: { d }
        }];
    },
    borderedRectContainer(params) {
        const aspectRatio = params.width / params.height;
        const radius = Math.min((params.radius || 0) / 100, aspectRatio);
        const borderWidth = params.borderWidth / 100 * Math.min(1, aspectRatio);
        const innerRadius = Math.max(0, radius - borderWidth);

        const spacerWidth = (1 - params.borderWidth / 100) * (params.spacerWidth / 100) * Math.min(1, aspectRatio);
        const usedSpacerWidth = borderWidth + spacerWidth;

        const pointCoords = [{ x: -aspectRatio, y: -1 }, { x: aspectRatio, y: -1 }, { x: aspectRatio, y: 1 }, { x: -aspectRatio, y: 1 }];
        const innerPointCoords = [
            { x: borderWidth - aspectRatio, y: borderWidth - 1 },
            { x: aspectRatio - borderWidth, y: borderWidth - 1 },
            { x: aspectRatio - borderWidth, y: 1 - borderWidth },
            { x: borderWidth - aspectRatio, y: 1 - borderWidth }
        ];
        innerPointCoords.reverse();

        const innerRectPoints = pointCoords.map(pt => ({
            x: pt.x * (1 - usedSpacerWidth / aspectRatio),
            y: pt.y * (1 - usedSpacerWidth)
        }));

        const innerRectRadius = Math.max(0, radius - usedSpacerWidth);

        return [{
            tag: 'path',
            attrs: {
                d: [
                    connectContainerPointsWithRadius(pointCoords, radius, aspectRatio),
                    connectContainerPointsWithRadius(innerPointCoords, innerRadius, aspectRatio),
                    connectContainerPointsWithRadius(innerRectPoints, innerRectRadius, aspectRatio)
                ].join('')
            }
        }];
    },
    borderedCircleContainer(params) {
        const borderWidth = params.borderWidth / 100;
        const innerRadius = 1 - borderWidth;
        const spacerWidth = (1 - params.borderWidth / 100) * (params.spacerWidth / 100);
        const usedSpacerWidth = borderWidth + spacerWidth;
        const innerCircleRadius = 1 - usedSpacerWidth;

        return [{
            tag: 'path',
            attrs: {
                d: `M0 -1A1 1 0 0 0 0 1A1 1 0 0 0 0 -1zM0 ${-innerRadius}A${innerRadius} ${innerRadius} 0 0 1 0 ${innerRadius}A${innerRadius} ${innerRadius} 0 0 1 0 ${-innerRadius}zM0 ${-innerCircleRadius}A${innerCircleRadius} ${innerCircleRadius} 0 0 0 0 ${innerCircleRadius}A${innerCircleRadius} ${innerCircleRadius} 0 0 0 0 ${-innerCircleRadius}z`
            }
        }];
    },
    polygonRingContainer(params) {
        const points = params && params.points || 6;
        const radius = (params.radius || 0) / 100;

        const borderWidth = params.borderWidth / 100;
        const innerRadius = 1 - borderWidth;
        const innerBorderRadius = Math.max(0, radius - borderWidth);

        const pointCoords = Array.apply(null, { length: points }).map((_, i) => {
            const deg = i * 360 / points;
            return rotateBy({ x: 0, y: - 1 }, { x: 0, y: 0 }, deg);
        });

        const innerPointCoords = pointCoords.map(pt => ({
            x: pt.x * innerRadius,
            y: pt.y * innerRadius
        }));
        innerPointCoords.reverse();
        return [{
            tag: 'path',
            attrs: {
                d: connectPointsWithRadius(pointCoords, radius) + connectPointsWithRadius(innerPointCoords, innerBorderRadius)
            }
        }];
    },
    borderedPolygonContainer(params) {
        const points = params && params.points || 6;
        const radius = (params.radius || 0) / 100;

        const borderWidth = params.borderWidth / 100;
        const innerRadius = 1 - borderWidth;
        const innerBorderRadius = Math.max(0, radius - borderWidth);

        const spacerWidth = (1 - params.borderWidth / 100) * (params.spacerWidth / 100);
        const usedSpacerWidth = borderWidth + spacerWidth;
        const innerPolygonRadius = 1 - usedSpacerWidth;
        const innerPolygonBorderRadius = Math.max(0, radius - usedSpacerWidth);

        const pointCoords = Array.apply(null, { length: points }).map((_, i) => {
            const deg = i * 360 / points;
            return rotateBy({ x: 0, y: - 1 }, { x: 0, y: 0 }, deg);
        });

        const innerPointCoords = pointCoords.map(pt => ({
            x: pt.x * innerRadius,
            y: pt.y * innerRadius
        }));

        const innerPolygonCoords = pointCoords.map(pt => ({
            x: pt.x * innerPolygonRadius,
            y: pt.y * innerPolygonRadius
        }));
        innerPointCoords.reverse();
        return [{
            tag: 'path',
            attrs: {
                d: connectPointsWithRadius(pointCoords, radius)
                    + connectPointsWithRadius(innerPointCoords, innerBorderRadius)
                    + connectPointsWithRadius(innerPolygonCoords, innerPolygonBorderRadius)
            }
        }];
    }
};

export const shapePreserveRatio = {
    arc: true,
    cat: true,
    star: true,
    polygon: true,
    heart: true,
    ring: true,
    rabbit: true,
    arcArrow: true,
    teardrop: true,
    cross: true,
    jigsaw: true,
    cloud: true,
    starContainer: true,
    arcContainer: true,
    ringContainer: true,
    heartContainer: true,
    crossContainer: true,
    jigsawContainer: true,
    borderedCircleContainer: true,
    polygonContainer: true,
    polygonRingContainer: true,
    borderedPolygonContainer: true
};

export const shapeParams = {
    arc: {
        stroke: '#333',
        strokeWidth: 1,
        innerRadius: .6,
        startAngle: -90,
        endAngle: 90,
        cornerRadius: 1
    },
    rect: {
        stroke: '#333',
        strokeWidth: 1,
        radius: 0
    },
    star: {
        stroke: '#333',
        strokeWidth: 1,
        innerRadius: .38,
        points: 5,
        radius: 0
    },
    polygon: {
        stroke: '#333',
        strokeWidth: 1,
        points: 6,
        radius: 0
    },
    heart: {
        stroke: '#333',
        strokeWidth: 1
    },
    ellipse: {
        stroke: '#333',
        strokeWidth: 1
    },
    cat: {
        color: '#fff',
        eyeColor: '#333',
        stroke: '#333',
        strokeWidth: 1
    },
    path: {
        stroke: '#333',
        strokeWidth: 1,
        shapeScale: .06,
        shapeX: -1,
        shapeY: -1,
        d: 'M27.578125,2.425 C27.553125,2.14375 27.2375,2.009375 27.2375,2.009375 C27.2375,2.009375 26.903125,1.959375 26.634375,2.109375 C26.175,2.365625 25.50625,2.6875 22.3125,6.584375 C20.090625,9.296875 17.346875,12.934375 15.75625,15 C14.565625,16.5375 14.490625,17.609375 14.74375,18.21875 C14.79375,18.3375 15.159375,18.878125 15.684375,19.190625 C16.065625,19.41875 16.55,19.453125 16.975,19.465625 C17.6625,19.4875 18.315625,18.996875 18.315625,18.996875 C18.315625,18.996875 18.6625,18.675 19.184375,17.934375 C20.253125,16.41875 22.1375,13.53125 23.771875,10.75 C25.84375,7.2375 27.38125,3.59375 27.38125,3.59375 C27.38125,3.59375 27.61875,2.8875 27.578125,2.425 Z M14.434375,20.565625 C13.36875,19.5875 11.940625,19.525 11.940625,19.525 C11.940625,19.525 10.565625,19.38125 9.646875,20.015625 C8.128125,21.0625 8.1,23.1875 8.0875,23.503125 C8.053125,24.465625 8.13125,25.625 7.54375,26.64375 C6.678125,28.14375 4.415625,28.76875 4.415625,28.76875 C4.415625,28.76875 4.8,29.234375 5.35,29.50625 C5.353125,29.509375 7.1,30.50625 10.70625,29.65 C11.690625,29.415625 12.78125,29.115625 13.665625,28.5625 C14.746875,27.8875 15.546875,26.928125 15.7625,26.453125 C16.7,24.421875 16.221875,22.20625 14.434375,20.565625 L14.434375,20.565625 Z'
    },
    ring: {
        stroke: '#333',
        strokeWidth: 1,
        innerRadius: .62
    },
    triangle: {
        stroke: '#333',
        strokeWidth: 1,
        pointPos: 0,
        radius: 0
    },
    diamond: {
        stroke: '#333',
        strokeWidth: 1,
        radius: 0
    },
    rabbit: {
        color: '#fff',
        eyeColor: '#333',
        stroke: '#333',
        strokeWidth: 1
    },
    trapezoid: {
        skewLeft: .5,
        skewRight: -.5,
        stroke: '#333',
        strokeWidth: 1,
        radius: 0
    },
    starContainer: {
        innerRadius: .38,
        points: 5
    },
    polygonContainer: {
        points: 6,
        radius: 0
    },
    ringContainer: {
        innerRadius: .62
    },
    arcContainer: {
        innerRadius: .6,
        startAngle: -90,
        endAngle: 90,
        cornerRadius: 1
    },
    rectContainer: {
        radius: 0
    },
    ellipseContainer: {},
    triangleContainer: {
        pointPos: 0
    },
    diamondContainer: {},
    heartContainer: {},
    arrow: {
        stroke: '#333',
        strokeWidth: 1,
        arrowWidth: .3,
        radius: 0
    },
    arcArrow: {
        stroke: '#333',
        strokeWidth: 1,
        outerRadius: .66,
        startAngle: -90,
        endAngle: 90,
        arrowWidth: .27,
        counterclockwise: false
    },
    teardrop: {
        stroke: '#333',
        strokeWidth: 1,
        controlPosition: 1
    },
    cloud: {
        stroke: '#333',
        strokeWidth: 1,
        controlPosition: 1
    },
    line: {
        stroke: '#333',
        strokeWidth: 1,
        shapeLinear: true
    },
    verticalLine: {
        stroke: '#333',
        strokeWidth: 1,
        shapeLinear: true,
        rotate: 90
    },
    trapezoidContainer: {
        skewLeft: .5,
        skewRight: -.5,
        radius: 0
    },
    chatBubble: {
        stroke: '#333',
        strokeWidth: 1,
        radius: 100
    },
    rectBorderContainer: {
        borderWidth: 40,
        radius: 0
    },
    cross: {
        stroke: '#333',
        strokeWidth: 1,
        radius: 0,
        controlPosition: 1
    },
    crossContainer: {
        radius: 0,
        controlPosition: 1
    },
    jigsaw: {
        stroke: '#333',
        strokeWidth: 1,
        horizontal: true,
        topEdge: false,
        rightEdge: false,
        bottomEdge: false,
        leftEdge: false
    },
    jigsawContainer: {
        horizontal: true,
        topEdge: false,
        rightEdge: false,
        bottomEdge: false,
        leftEdge: false
    },
    borderedRectContainer: {
        borderWidth: 10,
        radius: 0,
        spacerWidth: 11
    },
    borderedCircleContainer: {
        borderWidth: 10,
        spacerWidth: 11
    },
    polygonRingContainer: {
        points: 6,
        borderWidth: 30,
        radius: 0
    },
    borderedPolygonContainer: {
        points: 6,
        borderWidth: 10,
        spacerWidth: 11,
        radius: 0
    }
};

export const shapeParamDescs = {
    arc: [
        {
            tag: 'LabelInput',
            attrs: {
                max: 1,
                min: 0,
                step: .01,
                label: '内部半径'
            },
            prop: 'innerRadius'
        },
        {
            tag: 'LabelInput',
            attrs: {
                max: 1,
                step: .01,
                label: '角半径'
            },
            prop: 'cornerRadius'
        },
        {
            tag: 'LabelInput',
            attrs: {
                deg: true,
                label: '起始角度'
            },
            prop: 'startAngle'
        },
        {
            tag: 'LabelInput',
            attrs: {
                deg: true,
                label: '结束角度'
            },
            prop: 'endAngle'
        }
    ],
    rect: [
        {
            tag: 'LabelInput',
            attrs: {
                max: 100,
                min: 0,
                step: 1,
                percentage: true,
                label: '圆角半径',
                precision: 0
            },
            prop: 'radius'
        }
    ],
    ellipse: [],
    star: [
        {
            tag: 'LabelInput',
            attrs: {
                max: 1,
                step: .01,
                label: '内部半径'
            },
            prop: 'innerRadius'
        },
        {
            tag: 'LabelInput',
            attrs: {
                min: 3,
                max: 16,
                label: '顶点数'
            },
            prop: 'points'
        },
        {
            tag: 'LabelInput',
            attrs: {
                max: 100,
                min: 0,
                step: 1,
                percentage: true,
                label: '圆角半径',
                precision: 0
            },
            prop: 'radius'
        }
    ],
    polygon: [
        {
            tag: 'LabelInput',
            attrs: {
                min: 3,
                max: 32,
                label: '顶点数'
            },
            prop: 'points'
        },
        {
            tag: 'LabelInput',
            attrs: {
                max: 100,
                min: 0,
                step: 1,
                percentage: true,
                label: '圆角半径',
                precision: 0
            },
            prop: 'radius'
        }
    ],
    heart: [],
    cat: [
        {
            tag: 'LabelColor',
            attrs: {
                label: 'Eye Color'
            },
            prop: 'eyeColor'
        }
    ],
    path: [
        {
            tag: 'LabelInput',
            attrs: {
                min: 0,
                step: .001,
                label: 'Scale',
                precision: null
            },
            prop: 'shapeScale'
        },
        {
            tag: 'LabelInput',
            attrs: {
                step: .001,
                label: 'x'
            },
            prop: 'shapeX'
        },
        {
            tag: 'LabelInput',
            attrs: {
                step: .001,
                label: 'y'
            },
            prop: 'shapeY'
        }
    ],
    ring: [
        {
            tag: 'LabelInput',
            attrs: {
                max: 1,
                step: .01,
                min: 0,
                label: '内部半径'
            },
            prop: 'innerRadius'
        }
    ],
    triangle: [
        {
            tag: 'LabelInput',
            attrs: {
                max: 1,
                min: -1,
                step: .01,
                label: '顶点位置'
            },
            prop: 'pointPos'
        },
        {
            tag: 'LabelInput',
            attrs: {
                max: 100,
                min: 0,
                step: 1,
                percentage: true,
                label: '圆角半径',
                precision: 0
            },
            prop: 'radius'
        }
    ],
    diamond: [
        {
            tag: 'LabelInput',
            attrs: {
                max: 100,
                min: 0,
                step: 1,
                percentage: true,
                label: '圆角半径',
                precision: 0
            },
            prop: 'radius'
        }
    ],
    rabbit: [
        {
            tag: 'LabelColor',
            attrs: {
                label: 'Eye Color'
            },
            prop: 'eyeColor'
        }
    ],
    trapezoid: [
        {
            tag: 'LabelInput',
            attrs: {
                max: 2,
                min: -2,
                step: .01,
                label: '左侧边倾斜'
            },
            prop: 'skewLeft'
        },
        {
            tag: 'LabelInput',
            attrs: {
                max: 2,
                min: -2,
                step: .01,
                label: '右侧边倾斜'
            },
            prop: 'skewRight'
        },
        {
            tag: 'LabelInput',
            attrs: {
                max: 100,
                min: 0,
                step: 1,
                percentage: true,
                label: '圆角半径',
                precision: 0
            },
            prop: 'radius'
        }
    ],
    arrow: [
        {
            tag: 'LabelInput',
            attrs: {
                max: 1,
                min: 0,
                step: .01,
                label: '箭头宽度'
            },
            prop: 'arrowWidth'
        },
        {
            tag: 'LabelInput',
            attrs: {
                max: 100,
                min: 0,
                step: 1,
                percentage: true,
                label: '圆角半径',
                precision: 0
            },
            prop: 'radius'
        }
    ],
    arcArrow: [
        {
            tag: 'LabelInput',
            attrs: {
                max: 1,
                min: 0,
                step: .01,
                label: '外部半径'
            },
            prop: 'outerRadius'
        },
        {
            tag: 'LabelInput',
            attrs: {
                deg: true,
                label: '起始角度'
            },
            prop: 'startAngle'
        },
        {
            tag: 'LabelInput',
            attrs: {
                deg: true,
                label: '结束角度'
            },
            prop: 'endAngle'
        },
        {
            tag: 'LabelInput',
            attrs: {
                max: 1,
                min: 0,
                step: .01,
                label: '箭头宽度'
            },
            prop: 'arrowWidth'
        },
        {
            tag: 'LabelSwitch',
            attrs: {
                deg: true,
                label: '逆时针'
            },
            prop: 'counterclockwise'
        }
    ],
    teardrop: [
        {
            tag: 'LabelInput',
            attrs: {
                max: 2,
                min: 0,
                step: .01,
                label: '控制点位置'
            },
            prop: 'controlPosition'
        }
    ],
    chatBubble: [
        {
            tag: 'LabelInput',
            attrs: {
                max: 100,
                min: 0,
                step: 1,
                percentage: true,
                label: '圆角半径',
                precision: 0
            },
            prop: 'radius'
        }
    ],
    cross: [
        {
            tag: 'LabelInput',
            attrs: {
                max: 100,
                min: 0,
                step: 1,
                percentage: true,
                label: '圆角半径',
                precision: 0
            },
            prop: 'radius'
        },
        {
            tag: 'LabelInput',
            attrs: {
                max: 2,
                min: 0,
                step: .01,
                label: '控制点位置'
            },
            prop: 'controlPosition'
        }
    ],
    jigsaw: [
        {
            tag: 'LabelSwitch',
            prop: 'horizontal',
            attrs: { label: '水平方向' }
        },
        {
            tag: 'LabelSwitch',
            prop: 'topEdge',
            attrs: { label: '顶部边缘' }
        },
        {
            tag: 'LabelSwitch',
            prop: 'rightEdge',
            attrs: { label: '右侧边缘' }
        },
        {
            tag: 'LabelSwitch',
            prop: 'bottomEdge',
            attrs: { label: '底部边缘' }
        },
        {
            tag: 'LabelSwitch',
            prop: 'leftEdge',
            attrs: { label: '左侧边缘' }
        }
    ],
    cloud: [],
    line: [],
    verticalLine: [],
    starContainer: [
        {
            tag: 'LabelInput',
            attrs: {
                max: 1,
                step: .01,
                label: '内部半径'
            },
            prop: 'innerRadius'
        },
        {
            tag: 'LabelInput',
            attrs: {
                min: 3,
                max: 16,
                label: '顶点数'
            },
            prop: 'points'
        }
    ],
    polygonContainer: [
        {
            tag: 'LabelInput',
            attrs: {
                max: 100,
                min: 0,
                step: 1,
                percentage: true,
                label: '圆角半径',
                precision: 0
            },
            prop: 'radius'
        },
        {
            tag: 'LabelInput',
            attrs: {
                min: 3,
                max: 32,
                label: '顶点数'
            },
            prop: 'points'
        }
    ],
    ringContainer: [
        {
            tag: 'LabelInput',
            attrs: {
                max: 1,
                step: .01,
                min: 0,
                label: '内部半径'
            },
            prop: 'innerRadius'
        }
    ],
    arcContainer: [
        {
            tag: 'LabelInput',
            attrs: {
                max: 1,
                min: 0,
                step: .01,
                label: '内部半径'
            },
            prop: 'innerRadius'
        },
        {
            tag: 'LabelInput',
            attrs: {
                max: 1,
                step: .01,
                label: '角半径'
            },
            prop: 'cornerRadius'
        },
        {
            tag: 'LabelInput',
            attrs: {
                deg: true,
                label: '起始角度'
            },
            prop: 'startAngle'
        },
        {
            tag: 'LabelInput',
            attrs: {
                deg: true,
                label: '结束角度'
            },
            prop: 'endAngle'
        }
    ],
    rectContainer: [
        {
            tag: 'LabelInput',
            attrs: {
                max: 100,
                min: 0,
                step: 1,
                percentage: true,
                label: '圆角半径',
                precision: 0
            },
            prop: 'radius'
        }
    ],
    ellipseContainer: [],
    heartContainer: [],
    diamondContainer: [],
    triangleContainer: [
        {
            tag: 'LabelInput',
            attrs: {
                max: 1,
                min: -1,
                step: .01,
                label: '顶点位置'
            },
            prop: 'pointPos'
        }
    ],
    trapezoidContainer: [
        {
            tag: 'LabelInput',
            attrs: {
                max: 2,
                min: -2,
                step: .01,
                label: '左侧边倾斜'
            },
            prop: 'skewLeft'
        },
        {
            tag: 'LabelInput',
            attrs: {
                max: 2,
                min: -2,
                step: .01,
                label: '右侧边倾斜'
            },
            prop: 'skewRight'
        },
        {
            tag: 'LabelInput',
            attrs: {
                max: 100,
                min: 0,
                step: 1,
                percentage: true,
                label: '圆角半径',
                precision: 0
            },
            prop: 'radius'
        }
    ],
    rectBorderContainer: [
        {
            tag: 'LabelInput',
            attrs: {
                max: 100,
                min: 0,
                step: 1,
                percentage: true,
                label: '边框宽度',
                precision: 0
            },
            prop: 'borderWidth'
        },
        {
            tag: 'LabelInput',
            attrs: {
                max: 100,
                min: 0,
                step: 1,
                percentage: true,
                label: '圆角半径',
                precision: 0
            },
            prop: 'radius'
        }
    ],
    crossContainer: [
        {
            tag: 'LabelInput',
            attrs: {
                max: 100,
                min: 0,
                step: 1,
                percentage: true,
                label: '圆角半径',
                precision: 0
            },
            prop: 'radius'
        },
        {
            tag: 'LabelInput',
            attrs: {
                max: 2,
                min: 0,
                step: .01,
                label: '控制点位置'
            },
            prop: 'controlPosition'
        }
    ],
    jigsawContainer: [
        {
            tag: 'LabelSwitch',
            prop: 'horizontal',
            attrs: { label: '水平方向' }
        },
        {
            tag: 'LabelSwitch',
            prop: 'topEdge',
            attrs: { label: '顶部边缘' }
        },
        {
            tag: 'LabelSwitch',
            prop: 'rightEdge',
            attrs: { label: '右侧边缘' }
        },
        {
            tag: 'LabelSwitch',
            prop: 'bottomEdge',
            attrs: { label: '底部边缘' }
        },
        {
            tag: 'LabelSwitch',
            prop: 'leftEdge',
            attrs: { label: '左侧边缘' }
        }
    ],
    borderedRectContainer: [
        {
            tag: 'LabelInput',
            attrs: {
                max: 100,
                min: 0,
                step: 1,
                percentage: true,
                label: '边框宽度',
                precision: 0
            },
            prop: 'borderWidth'
        },
        {
            tag: 'LabelInput',
            attrs: {
                max: 100,
                min: 0,
                step: 1,
                percentage: true,
                label: '圆角半径',
                precision: 0
            },
            prop: 'radius'
        },
        {
            tag: 'LabelInput',
            attrs: {
                max: 100,
                min: 0,
                step: 1,
                percentage: true,
                label: '空白区域宽度',
                precision: 0
            },
            prop: 'spacerWidth'
        }
    ],
    borderedCircleContainer: [
        {
            tag: 'LabelInput',
            attrs: {
                max: 100,
                min: 0,
                step: 1,
                percentage: true,
                label: '边框宽度',
                precision: 0
            },
            prop: 'borderWidth'
        },
        {
            tag: 'LabelInput',
            attrs: {
                max: 100,
                min: 0,
                step: 1,
                percentage: true,
                label: '空白区域宽度',
                precision: 0
            },
            prop: 'spacerWidth'
        }
    ],
    polygonRingContainer: [
        {
            tag: 'LabelInput',
            attrs: {
                min: 3,
                max: 32,
                label: '顶点数'
            },
            prop: 'points'
        },
        {
            tag: 'LabelInput',
            attrs: {
                max: 100,
                min: 0,
                step: 1,
                percentage: true,
                label: '圆角半径',
                precision: 0
            },
            prop: 'radius'
        },
        {
            tag: 'LabelInput',
            attrs: {
                max: 100,
                min: 0,
                step: 1,
                percentage: true,
                label: '边框宽度',
                precision: 0
            },
            prop: 'borderWidth'
        }
    ],
    borderedPolygonContainer: [
        {
            tag: 'LabelInput',
            attrs: {
                min: 3,
                max: 32,
                label: '顶点数'
            },
            prop: 'points'
        },
        {
            tag: 'LabelInput',
            attrs: {
                max: 100,
                min: 0,
                step: 1,
                percentage: true,
                label: '圆角半径',
                precision: 0
            },
            prop: 'radius'
        },
        {
            tag: 'LabelInput',
            attrs: {
                max: 100,
                min: 0,
                step: 1,
                percentage: true,
                label: '边框宽度',
                precision: 0
            },
            prop: 'borderWidth'
        },
        {
            tag: 'LabelInput',
            attrs: {
                max: 100,
                min: 0,
                step: 1,
                percentage: true,
                label: '空白区域宽度',
                precision: 0
            },
            prop: 'spacerWidth'
        }
    ]
};

export const containerGrids = {
    v2({ w, h, shapeNames }) {
        const params = shapeParams.rectContainer;
        const split = Math.round(w / 2);
        return [
            {
                shape: 'rectContainer',
                top: 0,
                left: 0,
                width: split - 1,
                height: h,
                rotate: 0,
                brightness: 1,
                opacity: 100,
                scale: 1,
                name: shapeNames.rectContainer || '',
                ...params
            },
            {
                shape: 'rectContainer',
                top: 0,
                left: split + 1,
                width: w - split - 1,
                height: h,
                rotate: 0,
                brightness: 1,
                opacity: 100,
                scale: 1,
                name: shapeNames.rectContainer || '',
                ...params
            }
        ];
    },
    h2({ w, h, shapeNames }) {
        const params = shapeParams.rectContainer;
        const split = Math.round(h / 2);
        return [
            {
                shape: 'rectContainer',
                top: 0,
                left: 0,
                width: w,
                height: split - 1,
                rotate: 0,
                brightness: 1,
                opacity: 100,
                scale: 1,
                name: shapeNames.rectContainer || '',
                ...params
            },
            {
                shape: 'rectContainer',
                top: split + 2,
                left: 0,
                width: w,
                height: h - split - 1,
                rotate: 0,
                brightness: 1,
                opacity: 100,
                scale: 1,
                name: shapeNames.rectContainer || '',
                ...params
            }
        ];
    },
    o2({ w, h, shapeNames }) {
        const params = shapeParams.trapezoidContainer;
        const split = w / 2;
        const usedWidth = Math.round(split * 8 / 7 - 1.5);
        return [
            {
                shape: 'trapezoidContainer',
                top: 0,
                left: 0,
                width: usedWidth,
                height: h,
                rotate: 0,
                brightness: 1,
                opacity: 100,
                scale: 1,
                name: shapeNames.trapezoidContainer || '',
                ...params,
                skewLeft: 0,
                skewRight: .5
            },
            {
                shape: 'trapezoidContainer',
                top: 0,
                left: w - usedWidth,
                width: usedWidth,
                height: h,
                rotate: 0,
                brightness: 1,
                opacity: 100,
                scale: 1,
                name: shapeNames.trapezoidContainer || '',
                ...params,
                skewRight: 0
            }
        ];
    },
    o2d({ w, h, shapeNames }) {
        const params = shapeParams.trapezoidContainer;
        const split = w / 2;
        const usedWidth = Math.round(split * 8 / 7 - 1.5);
        return [
            {
                shape: 'trapezoidContainer',
                top: 0,
                left: 0,
                width: usedWidth,
                height: h,
                rotate: 0,
                brightness: 1,
                opacity: 100,
                scale: 1,
                name: shapeNames.trapezoidContainer || '',
                ...params,
                skewLeft: 0
            },
            {
                shape: 'trapezoidContainer',
                top: 0,
                left: w - usedWidth,
                width: usedWidth,
                height: h,
                rotate: 0,
                brightness: 1,
                opacity: 100,
                scale: 1,
                name: shapeNames.trapezoidContainer || '',
                ...params,
                skewLeft: -.5,
                skewRight: 0
            }
        ];
    },
    v3({ w, h, shapeNames }) {
        const params = shapeParams.rectContainer;
        const split = Math.round(w / 3 - 1);

        return [
            {
                shape: 'rectContainer',
                top: 0,
                left: 0,
                width: split,
                height: h,
                rotate: 0,
                brightness: 1,
                opacity: 100,
                scale: 1,
                name: shapeNames.rectContainer || '',
                ...params
            },
            {
                shape: 'rectContainer',
                top: 0,
                left: split + 2,
                width: split,
                height: h,
                rotate: 0,
                brightness: 1,
                opacity: 100,
                scale: 1,
                name: shapeNames.rectContainer || '',
                ...params
            },
            {
                shape: 'rectContainer',
                top: 0,
                left: w - split,
                width: split,
                height: h,
                rotate: 0,
                brightness: 1,
                opacity: 100,
                scale: 1,
                name: shapeNames.rectContainer || '',
                ...params
            }
        ];
    },
    h3({ w, h, shapeNames }) {
        const params = shapeParams.rectContainer;
        const split = Math.round(h / 3 - 1);

        return [
            {
                shape: 'rectContainer',
                top: 0,
                left: 0,
                width: w,
                height: split,
                rotate: 0,
                brightness: 1,
                opacity: 100,
                scale: 1,
                name: shapeNames.rectContainer || '',
                ...params
            },
            {
                shape: 'rectContainer',
                top: split + 2,
                left: 0,
                width: w,
                height: split,
                rotate: 0,
                brightness: 1,
                opacity: 100,
                scale: 1,
                name: shapeNames.rectContainer || '',
                ...params
            },
            {
                shape: 'rectContainer',
                top: h - split,
                left: 0,
                width: w,
                height: split,
                rotate: 0,
                brightness: 1,
                opacity: 100,
                scale: 1,
                name: shapeNames.rectContainer || '',
                ...params
            }
        ];
    },
    o3({ w, h, shapeNames }) {
        const params = shapeParams.trapezoidContainer;
        const split = Math.round(w * .4 - 1.5);
        return [
            {
                shape: 'trapezoidContainer',
                top: 0,
                left: 0,
                width: split,
                height: h,
                rotate: 0,
                brightness: 1,
                opacity: 100,
                scale: 1,
                name: shapeNames.trapezoidContainer || '',
                ...params,
                skewLeft: 0,
                skewRight: .5
            },
            {
                shape: 'trapezoidContainer',
                top: 0,
                left: .5 * w - split / 2,
                width: split,
                height: h,
                rotate: 0,
                brightness: 1,
                opacity: 100,
                scale: 1,
                name: shapeNames.trapezoidContainer || '',
                ...params,
                skewRight: .5
            },
            {
                shape: 'trapezoidContainer',
                top: 0,
                left: w - split,
                width: split,
                height: h,
                rotate: 0,
                brightness: 1,
                opacity: 100,
                scale: 1,
                name: shapeNames.trapezoidContainer || '',
                ...params,
                skewRight: 0
            }
        ];
    },
    o3d({ w, h, shapeNames }) {
        const params = shapeParams.trapezoidContainer;
        const split = Math.round(w * .4 - 1.5);
        return [
            {
                shape: 'trapezoidContainer',
                top: 0,
                left: 0,
                width: split,
                height: h,
                rotate: 0,
                brightness: 1,
                opacity: 100,
                scale: 1,
                name: shapeNames.trapezoidContainer || '',
                ...params,
                skewLeft: 0
            },
            {
                shape: 'trapezoidContainer',
                top: 0,
                left: .5 * w - split / 2,
                width: split,
                height: h,
                rotate: 0,
                brightness: 1,
                opacity: 100,
                scale: 1,
                name: shapeNames.trapezoidContainer || '',
                ...params,
                skewLeft: -.5
            },
            {
                shape: 'trapezoidContainer',
                top: 0,
                left: w - split,
                width: split,
                height: h,
                rotate: 0,
                brightness: 1,
                opacity: 100,
                scale: 1,
                name: shapeNames.trapezoidContainer || '',
                ...params,
                skewRight: 0,
                skewLeft: -.5
            }
        ];
    },
    o3ad({ w, h, shapeNames }) {
        const params = shapeParams.trapezoidContainer;
        const split = Math.round(w * .4 - 1.5);
        return [
            {
                shape: 'trapezoidContainer',
                top: 0,
                left: 0,
                width: split,
                height: h,
                rotate: 0,
                brightness: 1,
                opacity: 100,
                scale: 1,
                name: shapeNames.trapezoidContainer || '',
                ...params,
                skewLeft: 0,
                skewRight: .5
            },
            {
                shape: 'trapezoidContainer',
                top: 0,
                left: .5 * w - split / 2,
                width: split,
                height: h,
                rotate: 0,
                brightness: 1,
                opacity: 100,
                scale: 1,
                name: shapeNames.trapezoidContainer || '',
                ...params
            },
            {
                shape: 'trapezoidContainer',
                top: 0,
                left: w - split,
                width: split,
                height: h,
                rotate: 0,
                brightness: 1,
                opacity: 100,
                scale: 1,
                name: shapeNames.trapezoidContainer || '',
                ...params,
                skewRight: 0,
                skewLeft: -.5
            }
        ];
    },
    o3da({ w, h, shapeNames }) {
        const params = shapeParams.trapezoidContainer;
        const split = Math.round(w * .4 - 1.5);
        return [
            {
                shape: 'trapezoidContainer',
                top: 0,
                left: 0,
                width: split,
                height: h,
                rotate: 0,
                brightness: 1,
                opacity: 100,
                scale: 1,
                name: shapeNames.trapezoidContainer || '',
                ...params,
                skewLeft: 0
            },
            {
                shape: 'trapezoidContainer',
                top: 0,
                left: .5 * w - split / 2,
                width: split,
                height: h,
                rotate: 0,
                brightness: 1,
                opacity: 100,
                scale: 1,
                name: shapeNames.trapezoidContainer || '',
                ...params,
                skewLeft: -.5,
                skewRight: .5
            },
            {
                shape: 'trapezoidContainer',
                top: 0,
                left: w - split,
                width: split,
                height: h,
                rotate: 0,
                brightness: 1,
                opacity: 100,
                scale: 1,
                name: shapeNames.trapezoidContainer || '',
                ...params,
                skewRight: 0
            }
        ];
    },
    v4({ w, h, shapeNames }) {
        const params = shapeParams.rectContainer;
        const split = Math.round(w / 4 - 1);

        return [
            {
                shape: 'rectContainer',
                top: 0,
                left: 0,
                width: split,
                height: h,
                rotate: 0,
                brightness: 1,
                opacity: 100,
                scale: 1,
                name: shapeNames.rectContainer || '',
                ...params
            },
            {
                shape: 'rectContainer',
                top: 0,
                left: split + 2,
                width: split,
                height: h,
                rotate: 0,
                brightness: 1,
                opacity: 100,
                scale: 1,
                name: shapeNames.rectContainer || '',
                ...params
            },
            {
                shape: 'rectContainer',
                top: 0,
                left: 2 * split + 4,
                width: split,
                height: h,
                rotate: 0,
                brightness: 1,
                opacity: 100,
                scale: 1,
                name: shapeNames.rectContainer || '',
                ...params
            },
            {
                shape: 'rectContainer',
                top: 0,
                left: w - split,
                width: split,
                height: h,
                rotate: 0,
                brightness: 1,
                opacity: 100,
                scale: 1,
                name: shapeNames.rectContainer || '',
                ...params
            }
        ];
    },
    g4({ w, h, shapeNames }) {
        const params = shapeParams.rectContainer;
        const split = Math.round(w / 2 - 1);
        const vSplit = Math.round(h / 2 - 1);

        return [
            {
                top: 0,
                left: 0
            },
            {
                top: 0,
                left: w - split
            },
            {
                top: h - vSplit,
                left: w - split
            },
            {
                top: h - vSplit,
                left: 0
            }
        ].map(obj => {
            return {
                shape: 'rectContainer',
                width: split,
                height: vSplit,
                rotate: 0,
                brightness: 1,
                opacity: 100,
                scale: 1,
                name: shapeNames.rectContainer || '',
                ...obj,
                ...params
            }
        });
    },
    g6v({ w, h, shapeNames }) {
        const params = shapeParams.rectContainer;
        const split = Math.round(w / 3 - 1);
        const vSplit = Math.round(h / 2 - 1);

        return [
            {
                top: 0,
                left: 0
            },
            {
                top: 0,
                left: split + 2
            },
            {
                top: 0,
                left: w - split
            },
            {
                top: h - vSplit,
                left: w - split
            },
            {
                top: h - vSplit,
                left: split + 2
            },
            {
                top: h - vSplit,
                left: 0
            }
        ].map(obj => {
            return {
                shape: 'rectContainer',
                width: split,
                height: vSplit,
                rotate: 0,
                brightness: 1,
                opacity: 100,
                scale: 1,
                name: shapeNames.rectContainer || '',
                ...obj,
                ...params
            }
        });
    },
    g6h({ w, h, shapeNames }) {
        const params = shapeParams.rectContainer;
        const split = Math.round(w / 2 - 1);
        const vSplit = Math.round(h / 3 - 1);

        return [
            {
                top: 0,
                left: 0
            },
            {
                top: 0,
                left: w - split
            },
            {
                top: vSplit + 2,
                left: w - split
            },
            {
                top: h - vSplit,
                left: w - split
            },
            {
                top: h - vSplit,
                left: 0
            },
            {
                top: vSplit + 2,
                left: 0
            }
        ].map(obj => {
            return {
                shape: 'rectContainer',
                width: split,
                height: vSplit,
                rotate: 0,
                brightness: 1,
                opacity: 100,
                scale: 1,
                name: shapeNames.rectContainer || '',
                ...obj,
                ...params
            }
        });
    },
    m3({ w, h, shapeNames }) {
        const params = shapeParams.rectContainer;
        const split = Math.round(w / 2);
        const vSplit = Math.round(h / 2 - 1);
        return [
            {
                shape: 'rectContainer',
                top: 0,
                left: 0,
                width: split - 1,
                height: h,
                rotate: 0,
                brightness: 1,
                opacity: 100,
                scale: 1,
                name: shapeNames.rectContainer || '',
                ...params
            },
            {
                shape: 'rectContainer',
                top: 0,
                left: split + 1,
                width: w - split - 1,
                height: vSplit,
                rotate: 0,
                brightness: 1,
                opacity: 100,
                scale: 1,
                name: shapeNames.rectContainer || '',
                ...params
            },
            {
                shape: 'rectContainer',
                top: h - vSplit - 1,
                left: split + 1,
                width: w - split - 1,
                height: vSplit,
                rotate: 0,
                brightness: 1,
                opacity: 100,
                scale: 1,
                name: shapeNames.rectContainer || '',
                ...params
            }
        ];
    },
    m3r({ w, h, shapeNames }) {
        const params = shapeParams.rectContainer;
        const split = Math.round(w / 2);
        const vSplit = Math.round(h / 2 - 1);
        return [
            {
                shape: 'rectContainer',
                top: 0,
                left: 0,
                width: split - 1,
                height: vSplit,
                rotate: 0,
                brightness: 1,
                opacity: 100,
                scale: 1,
                name: shapeNames.rectContainer || '',
                ...params
            },
            {
                shape: 'rectContainer',
                top: h - vSplit - 1,
                left: 0,
                width: w - split - 1,
                height: vSplit,
                rotate: 0,
                brightness: 1,
                opacity: 100,
                scale: 1,
                name: shapeNames.rectContainer || '',
                ...params
            },
            {
                shape: 'rectContainer',
                top: 0,
                left: split + 1,
                width: w - split - 1,
                height: h,
                rotate: 0,
                brightness: 1,
                opacity: 100,
                scale: 1,
                name: shapeNames.rectContainer || '',
                ...params
            }
        ];
    },
    m3t({ w, h, shapeNames }) {
        const params = shapeParams.rectContainer;
        const split = Math.round(w / 2);
        const vSplit = Math.round(h / 2 - 1);
        return [
            {
                shape: 'rectContainer',
                top: 0,
                left: 0,
                width: w,
                height: vSplit,
                rotate: 0,
                brightness: 1,
                opacity: 100,
                scale: 1,
                name: shapeNames.rectContainer || '',
                ...params
            },
            {
                shape: 'rectContainer',
                top: h - vSplit - 1,
                left: 0,
                width: w - split - 1,
                height: vSplit,
                rotate: 0,
                brightness: 1,
                opacity: 100,
                scale: 1,
                name: shapeNames.rectContainer || '',
                ...params
            },
            {
                shape: 'rectContainer',
                top: h - vSplit - 1,
                left: split + 1,
                width: w - split - 1,
                height: vSplit,
                rotate: 0,
                brightness: 1,
                opacity: 100,
                scale: 1,
                name: shapeNames.rectContainer || '',
                ...params
            }
        ];
    },
    m3b({ w, h, shapeNames }) {
        const params = shapeParams.rectContainer;
        const split = Math.round(w / 2);
        const vSplit = Math.round(h / 2 - 1);
        return [
            {
                shape: 'rectContainer',
                top: 0,
                left: 0,
                width: w - split - 1,
                height: vSplit,
                rotate: 0,
                brightness: 1,
                opacity: 100,
                scale: 1,
                name: shapeNames.rectContainer || '',
                ...params
            },
            {
                shape: 'rectContainer',
                top: 0,
                left: split + 1,
                width: w - split - 1,
                height: vSplit,
                rotate: 0,
                brightness: 1,
                opacity: 100,
                scale: 1,
                name: shapeNames.rectContainer || '',
                ...params
            },
            {
                shape: 'rectContainer',
                top: h - vSplit - 1,
                left: 0,
                width: w,
                height: vSplit,
                rotate: 0,
                brightness: 1,
                opacity: 100,
                scale: 1,
                name: shapeNames.rectContainer || '',
                ...params
            }
        ];
    },
    m3o({ w, h, shapeNames }) {
        const params = shapeParams.trapezoidContainer;
        const vSplit = Math.round(h / 2 - 1);
        const usedWidth = Math.round(w * .59 - 1.5);
        const usedHalfWidth = Math.round(w / 2 - 1.5);
        return [
            {
                shape: 'trapezoidContainer',
                top: 0,
                left: 0,
                width: usedWidth,
                height: h,
                rotate: 0,
                brightness: 1,
                opacity: 100,
                scale: 1,
                name: shapeNames.trapezoidContainer || '',
                ...params,
                skewLeft: 0,
                skewRight: .62
            },
            {
                shape: 'trapezoidContainer',
                top: 0,
                left: w - usedHalfWidth,
                width: usedHalfWidth,
                height: vSplit - 1,
                rotate: 0,
                brightness: 1,
                opacity: 100,
                scale: 1,
                name: shapeNames.trapezoidContainer || '',
                ...params,
                skewLeft: .36,
                skewRight: 0
            },
            {
                shape: 'trapezoidContainer',
                top: h - vSplit,
                left: w - usedWidth - 2,
                width: usedWidth + 2,
                height: vSplit,
                rotate: 0,
                brightness: 1,
                opacity: 100,
                scale: 1,
                name: shapeNames.trapezoidContainer || '',
                ...params,
                skewLeft: .31,
                skewRight: 0
            }
        ];
    },
    m4({ w, h, shapeNames }) {
        const params = shapeParams.rectContainer;
        const split = Math.round(w / 3 - 1);
        const vSplit = Math.round(h / 3 - 1);
        return [
            {
                shape: 'rectContainer',
                top: 0,
                left: 0,
                width: w - split - 2,
                height: h,
                rotate: 0,
                brightness: 1,
                opacity: 100,
                scale: 1,
                name: shapeNames.rectContainer || '',
                ...params
            },
            {
                shape: 'rectContainer',
                top: 0,
                left: w - split,
                width: split,
                height: vSplit,
                rotate: 0,
                brightness: 1,
                opacity: 100,
                scale: 1,
                name: shapeNames.rectContainer || '',
                ...params
            },
            {
                shape: 'rectContainer',
                top: vSplit + 2,
                left: w - split,
                width: split,
                height: vSplit,
                rotate: 0,
                brightness: 1,
                opacity: 100,
                scale: 1,
                name: shapeNames.rectContainer || '',
                ...params
            },
            {
                shape: 'rectContainer',
                top: h - vSplit,
                left: w - split,
                width: split,
                height: vSplit,
                rotate: 0,
                brightness: 1,
                opacity: 100,
                scale: 1,
                name: shapeNames.rectContainer || '',
                ...params
            }
        ];
    },
    m4t({ w, h, shapeNames }) {
        const params = shapeParams.rectContainer;
        const split = Math.round(w / 3 - 1);
        const vSplit = Math.round(h / 3 - 1);
        return [
            {
                shape: 'rectContainer',
                top: 0,
                left: 0,
                width: w,
                height: h - vSplit - 2,
                rotate: 0,
                brightness: 1,
                opacity: 100,
                scale: 1,
                name: shapeNames.rectContainer || '',
                ...params
            },
            {
                shape: 'rectContainer',
                top: h - vSplit,
                left: 0,
                width: split,
                height: vSplit,
                rotate: 0,
                brightness: 1,
                opacity: 100,
                scale: 1,
                name: shapeNames.rectContainer || '',
                ...params
            },
            {
                shape: 'rectContainer',
                top: h - vSplit,
                left: split + 2,
                width: split,
                height: vSplit,
                rotate: 0,
                brightness: 1,
                opacity: 100,
                scale: 1,
                name: shapeNames.rectContainer || '',
                ...params
            },
            {
                shape: 'rectContainer',
                top: h - vSplit,
                left: w - split,
                width: split,
                height: vSplit,
                rotate: 0,
                brightness: 1,
                opacity: 100,
                scale: 1,
                name: shapeNames.rectContainer || '',
                ...params
            }
        ];
    },
    m4r({ w, h, shapeNames }) {
        const params = shapeParams.rectContainer;
        const split = Math.round(w / 3 - 1);
        const vSplit = Math.round(h / 3 - 1);
        return [
            {
                shape: 'rectContainer',
                top: 0,
                left: 0,
                width: split,
                height: vSplit,
                rotate: 0,
                brightness: 1,
                opacity: 100,
                scale: 1,
                name: shapeNames.rectContainer || '',
                ...params
            },
            {
                shape: 'rectContainer',
                top: vSplit + 2,
                left: 0,
                width: split,
                height: vSplit,
                rotate: 0,
                brightness: 1,
                opacity: 100,
                scale: 1,
                name: shapeNames.rectContainer || '',
                ...params
            },
            {
                shape: 'rectContainer',
                top: h - vSplit,
                left: 0,
                width: split,
                height: vSplit,
                rotate: 0,
                brightness: 1,
                opacity: 100,
                scale: 1,
                name: shapeNames.rectContainer || '',
                ...params
            },
            {
                shape: 'rectContainer',
                top: 0,
                left: split + 2,
                width: w - split - 2,
                height: h,
                rotate: 0,
                brightness: 1,
                opacity: 100,
                scale: 1,
                name: shapeNames.rectContainer || '',
                ...params
            }
        ];
    },
    m4b({ w, h, shapeNames }) {
        const params = shapeParams.rectContainer;
        const split = Math.round(w / 3 - 1);
        const vSplit = Math.round(h / 3 - 1);
        return [
            {
                shape: 'rectContainer',
                top: 0,
                left: 0,
                width: split,
                height: vSplit,
                rotate: 0,
                brightness: 1,
                opacity: 100,
                scale: 1,
                name: shapeNames.rectContainer || '',
                ...params
            },
            {
                shape: 'rectContainer',
                top: 0,
                left: split + 2,
                width: split,
                height: vSplit,
                rotate: 0,
                brightness: 1,
                opacity: 100,
                scale: 1,
                name: shapeNames.rectContainer || '',
                ...params
            },
            {
                shape: 'rectContainer',
                top: 0,
                left: w - split,
                width: split,
                height: vSplit,
                rotate: 0,
                brightness: 1,
                opacity: 100,
                scale: 1,
                name: shapeNames.rectContainer || '',
                ...params
            },
            {
                shape: 'rectContainer',
                top: vSplit + 2,
                left: 0,
                width: w,
                height: h - vSplit - 2,
                rotate: 0,
                brightness: 1,
                opacity: 100,
                scale: 1,
                name: shapeNames.rectContainer || '',
                ...params
            }
        ];
    },
    g5({ w, h, shapeNames }) {
        const params = shapeParams.rectContainer;
        const split = Math.round(w / 2 - 1);
        const vSplit = Math.round(h / 3 - 1);
        return [
            {
                shape: 'rectContainer',
                top: 0,
                left: 0,
                width: split,
                height: vSplit,
                rotate: 0,
                brightness: 1,
                opacity: 100,
                scale: 1,
                name: shapeNames.rectContainer || '',
                ...params
            },
            {
                shape: 'rectContainer',
                top: 0,
                left: w - split,
                width: split,
                height: vSplit,
                rotate: 0,
                brightness: 1,
                opacity: 100,
                scale: 1,
                name: shapeNames.rectContainer || '',
                ...params
            },
            {
                shape: 'rectContainer',
                top: vSplit + 2,
                left: 0,
                width: w,
                height: vSplit,
                rotate: 0,
                brightness: 1,
                opacity: 100,
                scale: 1,
                name: shapeNames.rectContainer || '',
                ...params
            },
            {
                shape: 'rectContainer',
                top: h - vSplit,
                left: 0,
                width: split,
                height: vSplit,
                rotate: 0,
                brightness: 1,
                opacity: 100,
                scale: 1,
                name: shapeNames.rectContainer || '',
                ...params
            },
            {
                shape: 'rectContainer',
                top: h - vSplit,
                left: w - split,
                width: split,
                height: vSplit,
                rotate: 0,
                brightness: 1,
                opacity: 100,
                scale: 1,
                name: shapeNames.rectContainer || '',
                ...params
            }
        ];
    }
};

export const containerGridIcons = [
    {
        name: 'v2',
        d: 'M-1.5 -1L-1.5 1L-.05 1L-.05 -1zM.05 -1L.05 1L1.5 1L1.5 -1z'
    },
    {
        name: 'h2',
        d: 'M-1.5 -1L-1.5 -.05L1.5 -.05L1.5 -1zM-1.5 .05L-1.5 1L1.5 1L1.5 .05z'
    },
    {
        name: 'o2',
        d: 'M-1.5 -1L-1.5 1L-.25 1L.15 -1zM.25 -1L-.15 1L1.5 1L1.5 -1z'
    },
    {
        name: 'o2d',
        d: 'M-1.5 -1L-1.5 1L.15 1L-.25 -1zM-.15 -1L.25 1L1.5 1L1.5 -1z'
    },
    {
        name: 'v3',
        d: 'M-1.5 -1L-1.5 1L-.55 1L-.55 -1zM-.45 -1L-.45 1L.45 1L.45 -1zM.55 -1L.55 1L1.5 1L1.5 -1z'
    },
    {
        name: 'h3',
        d: 'M-1.5 -1L-1.5 -.38L1.5 -.38L1.5 -1zM-1.5 -.28L-1.5 .28L1.5 .28L1.5 -.28zM-1.5 .38L-1.5 1L1.5 1L1.5 .38z'
    },
    {
        name: 'o3',
        d: 'M-1.5 -1L-1.5 1L-.65 1L-.35 -1zM-.25 -1L-.55 1L.25 1L.55 -1zM.65 -1L.35 1L1.5 1L1.5 -1z'
    },
    {
        name: 'o3d',
        d: 'M-1.5 -1L-1.5 1L-.35 1L-.65 -1zM-.55 -1L-.25 1L.55 1L.25 -1zM.35 -1L.65 1L1.5 1L1.5 -1z'
    },
    {
        name: 'o3ad',
        d: 'M-1.5 -1L-1.5 1L-.65 1L-.35 -1zM-.25 -1L-.55 1L.55 1L.25 -1zM.35 -1L.65 1L1.5 1L1.5 -1z'
    },
    {
        name: 'o3da',
        d: 'M-1.5 -1L-1.5 1L-.35 1L-.65 -1zM-.55 -1L-.25 1L.25 1L.55 -1zM.65 -1L.35 1L1.5 1L1.5 -1z'
    },
    {
        name: 'v4',
        d: 'M-1.5 -1L-1.5 1L-.8 1L-.8 -1zM-.7 -1L-.7 1L-.05 1L-.05 -1zM.05 -1L.05 1L.7 1L.7 -1zM.8 -1L.8 1L1.5 1L1.5 -1z'
    },
    {
        name: 'g4',
        d: 'M-1.5 -1L-1.5 -.05L-.05 -.05L-.05 -1zM.05 -1L.05 -.05L1.5 -.05L1.5 -1zM-1.5 .05L-1.5 1L-.05 1L-.05 .05zM.05 .05L.05 1L1.5 1L1.5 .05z'
    },
    {
        name: 'g5',
        d: 'M-1.5 -1L-1.5 -.4L-.05 -.4L-.05 -1zM.05 -1L.05 -.4L1.5 -.4L1.5 -1zM-1.5 -.3L-1.5 .3L1.5 .3L1.5 -.3zM-1.5 .4L-1.5 1L-.05 1L-.05 .4zM.05 .4L.05 1L1.5 1L1.5 .4z'
    },
    {
        name: 'g6v',
        d: 'M-1.5 -1L-1.5 -.05L-.55 -.05L-.55 -1zM-.45 -1L-.45 -.05L.45 -.05L.45 -1zM.55 -1L.55 -.05L1.5 -.05L1.5 -1zM-1.5 .05L-1.5 1L-.55 1L-.55 .05zM-.45 .05L-.45 1L.45 1L.45 .05zM.55 .05L.55 1L1.5 1L1.5 .05z'
    },
    {
        name: 'g6h',
        d: 'M-1.5 -1L-1.5 -.38L-.05 -.38L-.05 -1zM.05 -1L.05 -.38L1.5 -.38L1.5 -1zM.05 -.28L.05 .28L1.5 .28L1.5 -.28zM.05 .38L.05 1L1.5 1L1.5 .38zM-1.5 .38L-1.5 1L-.05 1L-.05 .38zM-1.5 -.28L-1.5 .28L-.05 .28L-.05 -.28z'
    },
    {
        name: 'm3',
        d: 'M-1.5 -1L-1.5 1L-.05 1L-.05 -1zM.05 -1L.05 -.05L1.5 -.05L1.5 -1zM.05 .05L.05 1L1.5 1L1.5 .05z'
    },
    {
        name: 'm3r',
        d: 'M-1.5 -1L-1.5 -.05L-.05 -.05L-.05 -1zM.05 -1L.05 1L1.5 1L1.5 -1zM-1.5 .05L-1.5 1L-.05 1L-.05 .05z'
    },
    {
        name: 'm3t',
        d: 'M-1.5 -1L-1.5 -.05L1.5 -.05L1.5 -1zM.05 .05L.05 1L1.5 1L1.5 .05zM-1.5 .05L-1.5 1L-.05 1L-.05 .05z'
    },
    {
        name: 'm3b',
        d: 'M-1.5 .05L-1.5 1L1.5 1L1.5 .05zM.05 -1L.05 -.05L1.5 -.05L1.5 -1zM-1.5 -1L-1.5 -.05L-.05 -.05L-.05 -1z'
    },
    {
        name: 'm3o',
        d: 'M-1.5 -1L-1.5 1L-.25 1L.15 -1zM.25 -1L.06 -.05L1.5 -.05L1.5 -1zM.04 .05L-.15 1L1.5 1L1.5 .05z'
    },
    {
        name: 'm4',
        d: 'M-1.5 -1L-1.5 1L.15 1L.15 -1zM.25 -1L.25 -.4L1.5 -.4L1.5 -1zM.25 -.3L.25 .3L1.5 .3L1.5 -.3zM.25 .4L.25 1L1.5 1L1.5 .4z'
    },
    {
        name: 'm4t',
        d: 'M-1.5 -1L-1.5 .2L1.5 .2L1.5 -1zM-1.5 .3L-1.5 1L-.57 1L-.57 .3zM-.47 .3L-.47 1L.47 1L.47 .3zM.57 .3L.57 1L1.5 1L1.5 .3z'
    },
    {
        name: 'm4r',
        d: 'M-.15 -1L-.15 1L1.5 1L1.5 -1zM-1.5 -1L-1.5 -.4L-.25 -.4L-.25 -1zM-1.5 -.3L-1.5 .3L-.25 .3L-.25 -.3zM-1.5 .4L-1.5 1L-.25 1L-.25 .4z'
    },
    {
        name: 'm4b',
        d: 'M-1.5 -.2L-1.5 1L1.5 1L1.5 -.2zM-1.5 -1L-1.5 -.3L-.57 -.3L-.57 -1zM-.47 -1L-.47 -.3L.47 -.3L.47 -1zM.57 -1L.57 -.3L1.5 -.3L1.5 -1z'
    }
];